import React, {useEffect, Suspense, lazy} from 'react'
import PropTypes from 'prop-types'
import {Router, Switch, Route, useLocation} from 'react-router-dom'
import {connect} from 'react-redux'
import {compose} from 'redux'
import {Layout} from 'antd'
import {useResizeDetector} from 'react-resize-detector'
import {history} from '../../Utils/utils'
import i18n from '../../i18n'

// COMPONENTS
import StartupPage from '../StartupPage'
import LoginPage from '../LoginPage'
import SignUpPage from '../SignUpPage'
import NotFoundPage from '../NotFoundPage'
import CheckoutSuccessPage from '../CheckoutSuccessPage'

import openNotification from '../../Components/Notification'

// REDUCERS
import NavActions from '../../Redux/NavRedux'
import ErrorsActions from '../../Redux/ErrorsRedux'
import AuthActions from '../LoginPage/reducer'
import StartupCreators from '../../Redux/StartupRedux'

// HOC
import useWindowSize from '../../hooks/use-window-size'
import withAuth from '../../hoc/withAuth'

import {PrivateRoute} from '../../Components/PrivateRoute'
import ExternalPages from './ExternalPages'

const InternalPages = lazy(() => import('./InternalPages'))

function Routes(props) {
  const {
    auth,
    navSetProp,
    userInfo,
    error,
    verifyToken,
    startup,
    dispatch,
    isAdmin,
    isPasswordCreated,
  } = props

  const {width, ref} = useResizeDetector()
  const [, innerHeight] = useWindowSize()
  const {pathname} = useLocation()

  // STARTUP
  useEffect(() => {
    dispatch(StartupCreators.startup())
  }, [])

  useEffect(() => {
    if (innerHeight) document.documentElement.style.setProperty('--app-height', `${innerHeight}px`)
  }, [innerHeight])

  // REFRESH TOKEN
  useEffect(() => {
    if (auth?.accessToken && auth?.userInfo?.onboarding_finished) {
      verifyToken(auth.accessToken)
    }
  }, [auth?.accessToken, auth?.refreshToken])

  // ERRORS
  useEffect(() => {
    if (error) {
      if (error.message) {
        for (const i in error.message) {
          //message.error(`${i}: ${error.message[i]}`, 5);
          openNotification({
            type: 'error',
            message: `${i}: ${error.message[i]}`,
            style: {minWidth: '716px'},
            getContainer: userInfo?.onboarding_finished ? document.getElementById('global-wrap') : document.body,
            duration: 5,
            key: i,
          })
        }
      } else if (typeof error === 'string') {
        //message.error(error, 5);
        openNotification({
          type: 'error',
          message: error,
          style: {minWidth: '716px'},
          getContainer: userInfo?.onboarding_finished ? document.getElementById('global-wrap') : document.body,
          duration: 5,
          key: 'error notification',
        })
      } else {
        for (const i in error) {
          //message.error(`${i}: ${error[i]}`, 5);
          openNotification({
            type: 'error',
            message: `${i}: ${error[i]}`,
            style: {minWidth: '716px'},
            getContainer: userInfo?.onboarding_finished ? document.getElementById('global-wrap') : document.body,
            duration: 5,
            key: i,
          })
        }
      }
      props.errorReset()
    }
  }, [!!error])

  // RESIZE
  useEffect(() => {
    const correctWidth = width || window.innerWidth
    if (correctWidth > 768) {
      navSetProp('isDesktop', true)
      navSetProp('isMobile', false)
    } else {
      navSetProp('isDesktop', false)
      navSetProp('isMobile', true)
    }
  }, [width, !!userInfo])

  // SET LANGUAGE
  useEffect(() => {
    i18n.changeLanguage(userInfo?.language || 'en')
  }, [userInfo?.language])

  useEffect(() => {
    if (isPasswordCreated && !pathname.includes('/sign-up/confirm/')) props.logout()
  }, [pathname, isPasswordCreated])

  if (startup?.success === false && !auth.isValidRefreshToken) {
    return (
      <Layout>
        <Route path="*" component={StartupPage}/>
      </Layout>
    )
  }

  return (
    <Router history={history}>
      <Layout className="main-layout" ref={ref}>
        <Switch>
          <Route exact path="/" component={StartupPage}/>
          <Route path="/login" component={LoginPage}/>
          <Route exact path="/sign-up" component={SignUpPage}/>
          <Route path="/add-email" component={SignUpPage}/>

          <PrivateRoute
            exact
            path="/onboarding/checkout/success"
            redirectPath="/login"
            auth={!!auth?.accessToken && userInfo?.['onboarding_finished'] && !isAdmin}
            component={CheckoutSuccessPage}
          />

          <Route
            path={[
              '/check-mailbox',
              '/sign-up/confirm/:hash',
              '/restore-password',
              '/verify-code',
              '/restore-password/:hash',
              '/success/:hash',
              '/success',
              '/expired',
              '/already-registered',
              '/onboarding/plan',
              '/onboarding/checkout/:mode/:intentId',
              '/onboarding/checkout/:mode',
            ]}
            component={ExternalPages}
          />

          <Suspense fallback={null}>
            <Route
              path={[
                "/dashboard",
                "/excel",
                "/drag",
                "/admin/customers",
                "/admin/leads",
                "/settings/payment/:intentId",
                "/settings/:hash",
                "/settings",
              ]}
              component={InternalPages}
            />
          </Suspense>
          <Route path="/*" component={NotFoundPage}/>
        </Switch>
      </Layout>
    </Router>
  )
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  userInfo: state.auth.userInfo,
  isAdmin: state.auth.isAdmin,
  isPasswordCreated: state.auth.isPasswordCreated,
  startup: state.startup,
  isDesktop: state.nav.isDesktop,
  isMobile: state.nav.isMobile,
  error: state.errors.data,
})

const mapDispatchToProps = (dispatch) => ({
  navSetProp: (key, value) => dispatch(NavActions.navSetProp(key, value)),
  errorReset: () => dispatch(ErrorsActions.errorReset()),
  refreshToken: (token) => dispatch(AuthActions.refreshTokenRequest({token})),
  refreshTokenFailure: (data) => dispatch(AuthActions.refreshTokenFailure(data)),
  logout: () => dispatch(AuthActions.logout()),
  verifyToken: (token) => dispatch(AuthActions.verifyTokenRequest({token})),
  dispatch,
})

Routes.propTypes = {
  auth: PropTypes.shape({
    accessToken: PropTypes.string,
    refreshToken: PropTypes.string,
    isValidRefreshToken: PropTypes.bool,
  }),
  userInfo: PropTypes.shape({
    onboarding_finished: PropTypes.bool,
  }),
  startup: PropTypes.shape({
    success: PropTypes.bool,
  }),
  isAdmin: PropTypes.bool,
  isDesktop: PropTypes.bool,
  isMobile: PropTypes.bool,
  error: PropTypes.any,
  dispatch: PropTypes.func,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(compose(withAuth)(Routes))
